import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  AccountRestoreCard,
  type RestoreState,
} from '@/components/features/account/AccountRestoreCard';
import { AuthCard, AuthLayout } from '@/components/features/auth/AuthLayout';
import {
  resolveRestoreError,
  RESTORE_MISSING_TOKEN,
} from '@/lib/account/restore-error';
import { accountRecoveryApi } from '@/lib/api-client';

/**
 * `/account/restore` - the landing page for a recovery link sent to a
 * soft-deleted account inside its 30-day window.
 *
 * The path matters: `request-restore` mails
 * `{frontend_url}/account/restore?token=...`, and `routes.tsx` mounts it there.
 * It sits under `pages/account/` alongside Settings rather than `pages/auth/`,
 * because account recovery is its own surface - it just borrows the auth shell
 * (`AuthLayout` / `AuthStatusCard`), which stays in `features/auth/` as the
 * thing all five non-form auth screens share.
 *
 * On the shared `AuthStatusCard`, which retires this page's three bare 40px
 * icons (`text-green-500`, `text-red-500`, `text-muted-foreground` with no
 * container) and its inverted stack, where the heading sat in `CardHeader`
 * above an icon living in `CardContent`.
 *
 * The error branching lives in `lib/account/restore-error.ts` - it is a pure
 * status-to-copy lookup and is unit-tested there.
 */
/**
 * Fires the restore exactly once.
 *
 * The `hasRunRef` guard is load-bearing under React 18 StrictMode, which
 * double-invokes effects in development - without it the token is redeemed
 * twice and the second call fails against an already-restored account.
 */
function useAccountRestore(token: string | null) {
  const [state, setState] = useState<RestoreState>({ status: 'idle' });
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    if (!token) {
      setState({ status: 'error', ...RESTORE_MISSING_TOKEN });
      return;
    }

    setState({ status: 'restoring' });
    accountRecoveryApi
      .restore(token)
      .then(result => setState({ status: 'success', email: result.email }))
      .catch((err: unknown) =>
        setState({ status: 'error', ...resolveRestoreError(err) })
      );
  }, [token]);

  return state;
}

export function AccountRestorePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = useAccountRestore(searchParams.get('token'));

  return (
    <AuthLayout>
      <AuthCard>
        <AccountRestoreCard
          state={state}
          onGoToSignIn={() => navigate('/auth')}
        />
      </AuthCard>
    </AuthLayout>
  );
}
